import React from 'react';
import classNames from 'classnames';
import { Icon } from 'js/design_system/Icon';
import { BasePillProps } from 'js/design_system/PillButton';
import { trackClick } from 'js/utils/trackClick';

import 'app/assets/stylesheets/design_system/pill_button.scss';

type PillButtonLinkProps = {
  external?: boolean;
} & BasePillProps &
  React.ComponentPropsWithoutRef<'a'>;

/**
 * A pill-shaped link for tags that navigate to other pages
 *
 * Props:
 * - `size`: Size of the button - one of 'sm' or 'lg' (default: 'lg')
 * - `icon`: The name of a Material Symbols icon (string) or an arbitrary React component to render as
 *     the button's icon (left of the text)
 * - `iconRight`: The name of a Material Symbols icon (string) or an arbitrary React component to render
 *     as the button's icon (right of the text)
 * - `external`: Whether the link should act as an external link (default: false)
 * - `trackClick`: Options for tracking clicks on this button link
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { PillButtonLink } from 'js/design_system/link/PillButtonLink';
 *
 * <PillButtonLink href='#'>
 *   Click me!
 * </PillButtonLink>
 * ```
 */
export const PillButtonLink = React.forwardRef<
  HTMLAnchorElement,
  PillButtonLinkProps
>(
  (
    {
      size = 'lg',
      icon,
      iconRight,
      external,
      className,
      children,
      trackClick: trackClickOptions,
      onClick,
      rel: relProp,
      ...restProps
    },
    ref,
  ) => {
    const classes = classNames('ds-pill-button', className, {
      'ds-pill-button--small': size === 'sm',
    });

    let iconView = null;
    if (icon) {
      iconView =
        typeof icon === 'string' ? (
          <Icon name={icon} size={size === 'sm' ? 16 : 20} />
        ) : (
          icon
        );
    }

    let iconRightView = null;
    if (iconRight) {
      iconRightView =
        typeof iconRight === 'string' ? (
          <Icon name={iconRight} size={size === 'sm' ? 16 : 20} />
        ) : (
          iconRight
        );
    } else if (external) {
      iconRightView = (
        <Icon name="arrow_outward" size={size === 'sm' ? 16 : 20} />
      );
    }

    const rel = [relProp, external ? 'noreferrer' : ''].filter(Boolean);

    const handleClick = trackClickOptions
      ? (event: React.MouseEvent<HTMLAnchorElement>) => {
          if (trackClickOptions) {
            trackClick(trackClickOptions);
          }
          return onClick?.(event);
        }
      : onClick;

    return (
      // This IS the design system implementation:
      // eslint-disable-next-line no-restricted-syntax
      <a
        target={external ? '_blank' : undefined}
        className={classes}
        {...restProps}
        rel={rel.length > 0 ? rel.join(' ') : undefined}
        onClick={handleClick}
        ref={ref}
      >
        {iconView ? iconView : null}
        {children}
        {iconRightView ? iconRightView : null}
      </a>
    );
  },
);
